import { useEffect, useState } from 'react';
import { RefreshCw, Save, Link2 } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { useStore } from '../store/useStore';
import { formatDateTime } from '../lib/utils';
import { syncAllAndWait, useClickUpStatus } from '../lib/clickupStatus';

/** Admin: ClickUp connection, internal hourly cost, manual full sync. */
export default function ClickUpSettingsCard() {
  const { status, refresh } = useClickUpStatus();
  const projects = useStore((s) => s.projects);
  const [cost, setCost] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (status) setCost(String(status.hourlyCost));
  }, [status?.hourlyCost]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkedCount = projects.filter((p) => p.clickupListId).length;
  const running = syncing || !!status?.syncing;

  const saveCost = async () => {
    const v = Number(cost);
    if (!(v > 0)) {
      setError('Enter a positive hourly cost.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await apiClient.patch('/clickup/settings', { hourlyCost: v });
      await refresh();
      setMessage('Hourly cost saved.');
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    setError('');
    setMessage('');
    try {
      await syncAllAndWait();
      await refresh();
      setMessage('Sync finished.');
    } catch (e) {
      setError((e as Error)?.message ?? 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Link2 size={16} className="text-gray-400" />
          <h2 className="font-semibold text-gray-900 text-sm">ClickUp · Profitability</h2>
          {status && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {status.configured ? 'Connected' : 'Not connected'}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">{linkedCount} linked project{linkedCount === 1 ? '' : 's'}</span>
      </div>

      {status && !status.configured && (
        <p className="text-xs text-gray-500 mb-4">
          Add <code className="bg-gray-100 px-1 rounded">CLICKUP_API_TOKEN</code> (ClickUp → Settings → Apps) to the server's
          <code className="bg-gray-100 px-1 rounded ml-1">.env</code> and restart the API.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Internal cost per hour (€)</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              step={1}
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void saveCost(); }}
              className="w-28 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              onClick={saveCost}
              disabled={saving || !status || Number(cost) === status.hourlyCost}
              className="flex items-center gap-1.5 px-3 py-2 text-xs bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50"
            >
              <Save size={12} /> Save
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">Used for cost of time, margin and effective rate.</p>
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Hours sync</label>
          <div className="flex items-center gap-2">
            <button
              onClick={syncAll}
              disabled={running || !status?.configured}
              className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              <RefreshCw size={12} className={running ? 'animate-spin' : ''} />
              {running ? 'Syncing…' : 'Sync all now'}
            </button>
            <span className="text-xs text-gray-400">
              Last: {status?.lastSync ? formatDateTime(status.lastSync) : 'never'}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Runs automatically once a day.</p>
        </div>
      </div>

      {message && <p className="mt-3 text-xs text-green-600">{message}</p>}
      {error && <p className="mt-3 text-xs text-red-500">{error}</p>}
    </div>
  );
}
